import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Alert, Box, Snackbar, Stack, TextField, Typography } from '@mui/material';
import { FlowBreadcrumbs, FlowFormCard, SubmitButton, mediaBaseUrl, mediaPreviewStyles } from './FlowLayout';
import { fetchDestinationHierarchy } from './flowHierarchy';

const EditGallery = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [destinationsId, setDestinationsId] = useState('');
  const [hierarchy, setHierarchy] = useState({
    destinationName: '',
    tourTypeId: '',
    tourTypeName: '',
    continentId: '',
    continentName: ''
  });
  const [src, setSrc] = useState(null);
  const [currentSrc, setCurrentSrc] = useState('');
  const [srcPreview, setSrcPreview] = useState(null);
  const [alt, setAlt] = useState('');
  const [imageOrder, setImageOrder] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    axios.get(`https://aaryanholidays.in/backend/gallery/item/${id}`)
      .then((response) => {
        const galleryItem = response.data;
        setDestinationsId(galleryItem.destinations_id);
        setCurrentSrc(galleryItem.src);
        setAlt(galleryItem.alt);
        setImageOrder(galleryItem.image_order);
        return fetchDestinationHierarchy(galleryItem.destinations_id);
      })
      .then((response) => {
        if (response) {
          setHierarchy(response);
        }
      })
      .catch((error) => console.error('There was an error fetching the gallery item!', error));
  }, [id]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    const formData = new FormData();
    formData.append('destinations_id', destinationsId);
    formData.append('type', 'img');
    if (src) {
      formData.append('src', src);
    }
    formData.append('alt', alt);
    formData.append('image_order', imageOrder);

    try {
      const response = await axios.put(`https://aaryanholidays.in/backend/gallery/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSnackbar({ open: true, message: 'Gallery item updated successfully.', severity: 'success' });
      navigate(`/manage-destination/gallery/${response.data.destinations_id}`);
    } catch (error) {
      console.error('There was an error updating the gallery item!', error);
      setSnackbar({ open: true, message: 'There was an error updating the gallery item.', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <FlowBreadcrumbs items={[{ label: 'Tour Management', to: '/manage-continents' }, { label: hierarchy.continentName || 'Continent', to: hierarchy.continentId ? `/manage-tour-type/${hierarchy.continentId}` : undefined }, { label: hierarchy.tourTypeName || 'Country', to: hierarchy.tourTypeId ? `/manage-destination/${hierarchy.tourTypeId}` : undefined }, { label: hierarchy.destinationName || 'Package', to: hierarchy.tourTypeId ? `/manage-destination/${hierarchy.tourTypeId}` : undefined }, { label: 'Gallery', to: destinationsId ? `/manage-destination/gallery/${destinationsId}` : undefined }, { label: 'Edit Media' }]} />
      <FlowFormCard title="Edit Gallery Media" subtitle="Replace or refine gallery media while keeping order and alt text consistent for the package page.">
        <form onSubmit={handleSubmit}>
          <Stack spacing={2.5}>
            <Box>
              <input id="edit-gallery-upload" type="file" hidden accept="image/*" onChange={(e) => {
                const file = e.target.files[0];
                setSrc(file);
                setSrcPreview(file ? URL.createObjectURL(file) : null);
              }} />
              <label htmlFor="edit-gallery-upload">
                <SubmitButton component="span" variant="outlined">
                  Replace Image
                </SubmitButton>
              </label>
            </Box>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              {currentSrc ? (
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, color: '#64748b' }}>Current Image</Typography>
                  <img src={`${mediaBaseUrl}${currentSrc}`} alt="Current gallery" style={mediaPreviewStyles} />
                </Box>
              ) : null}
              {srcPreview ? (
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, color: '#64748b' }}>New Preview</Typography>
                  <img src={srcPreview} alt="New gallery preview" style={mediaPreviewStyles} />
                </Box>
              ) : null}
            </Stack>
            <TextField label="Alt Text" value={alt} onChange={(e) => setAlt(e.target.value)} fullWidth required />
            <TextField label="Image Order" value={imageOrder} onChange={(e) => setImageOrder(e.target.value)} fullWidth required />
            <SubmitButton type="submit" variant="contained" loading={submitting}>
              Update Media
            </SubmitButton>
          </Stack>
        </form>
      </FlowFormCard>
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditGallery;
